/**
 * "It's someone else": the user's own word on an extra cast photo that the
 * same-person check matched to the main photo.
 *
 * classifyAdditionalCharacterImage stamps `sameAsLead` on every extra
 * upload's brief profile, and the brief, the shot planner and the storyboard
 * then fold that photo into the lead. The verdict over-matches look-alikes:
 * tnYih99bPIAvMZF5JGJJk (Sep 28 2026, iOS smart flow, no prompt) cropped the
 * user's blond friend out of a group photo, the crop was folded into the
 * lead, and the video came out solo. The Sep 29 2026 fleet audit
 * (scripts/db-read/sameaslead-fleet-audit.mts) found at least 24 wrong folds
 * in 1,052 same-person verdicts over 30 days (2.3%, up to ~10% counting
 * pairs nobody can decide from the photos), about four times as often on
 * mobile as on web, and no second vision opinion separated look-alikes from
 * real second photos of the lead. The prompt-words check
 * (sameAsLeadIntent.ts) only helps users who wrote a prompt; only the user
 * knows, and without one they had no way to say it.
 *
 * So the composers run the same check at upload
 * (director.checkCastPhotoSameAsLead) and, on a match, show "Looks like the
 * same person as your main photo" with an "It's someone else" toggle. The
 * answer is stored per photo URL in the project metadata
 * (referenceImagesJson.castIdentity) together with the main photo it was
 * given against, and it outranks the vision verdict wherever profiles are
 * made: proposeBrief and the storyboard's profile backfill. An answer only
 * counts for the two photos it was given about (either way round): a
 * replaced photo or a different main photo falls back to the vision check,
 * the same URL-match staleness guard every profile consumer applies.
 *
 * Pure, so the server, the web composers and the mobile app share one rule;
 * the English copy lives here too (mobile passes it to t() as defaultValue).
 */

import { isStockCharacterPresetUrl } from "./characterPresets";
import { castPhotoSplitSiblings } from "./multiPersonCastPhoto";

export type CastIdentityAnswer = "someone_else" | "same_as_lead";

export type CastIdentityEntry = {
  identity: CastIdentityAnswer;
  /** The main photo the user compared this photo with. */
  leadImageUrl: string;
};

/** referenceImagesJson.castIdentity: the user's answer per extra photo URL. */
export type CastIdentityMap = Record<string, CastIdentityEntry>;

/** One answer per cast photo at most (the Director keeps up to 12). */
export const MAX_CAST_IDENTITY_ENTRIES = 12;

const MAX_URL_LENGTH = 2048;

function photoUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const url = value.trim();
  return url.length <= MAX_URL_LENGTH && /^https?:\/\//i.test(url)
    ? url
    : null;
}

function isCastIdentityAnswer(value: unknown): value is CastIdentityAnswer {
  return value === "someone_else" || value === "same_as_lead";
}

function ownEntry(
  map: CastIdentityMap,
  url: string,
): CastIdentityEntry | undefined {
  return Object.prototype.hasOwnProperty.call(map, url) ? map[url] : undefined;
}

/**
 * Tolerant read of a castIdentity map (stored metadata or a client payload):
 * malformed entries are dropped, never thrown. With `photos`, only answers
 * whose two photos are both still in the cast are kept.
 */
export function normalizeCastIdentity(
  raw: unknown,
  photos?: Iterable<string | null | undefined>,
): CastIdentityMap {
  const out: CastIdentityMap = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  const allowed = photos
    ? new Set(
        Array.from(photos, photoUrl).filter((url): url is string => !!url),
      )
    : null;
  let kept = 0;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (kept >= MAX_CAST_IDENTITY_ENTRIES) break;
    const imageUrl = photoUrl(key);
    if (!imageUrl || !value || typeof value !== "object") continue;
    const entry = value as Record<string, unknown>;
    const leadImageUrl = photoUrl(entry.leadImageUrl);
    if (!isCastIdentityAnswer(entry.identity) || !leadImageUrl) continue;
    if (leadImageUrl === imageUrl) continue;
    if (allowed && !(allowed.has(imageUrl) && allowed.has(leadImageUrl))) {
      continue;
    }
    out[imageUrl] = { identity: entry.identity, leadImageUrl };
    kept += 1;
  }
  return out;
}

/**
 * The user's answer about the photo `imageUrl` next to the main photo
 * `leadImageUrl`, whichever of the two it was given for: an answer given
 * before the photos swapped places still describes the same two people.
 * Null when the user never answered for this pair.
 */
export function castIdentityAnswer(
  map: CastIdentityMap | null | undefined,
  imageUrl: string | null | undefined,
  leadImageUrl: string | null | undefined,
): CastIdentityAnswer | null {
  if (!map || !imageUrl || !leadImageUrl || imageUrl === leadImageUrl) {
    return null;
  }
  const direct = ownEntry(map, imageUrl);
  if (
    direct?.leadImageUrl === leadImageUrl &&
    isCastIdentityAnswer(direct.identity)
  ) {
    return direct.identity;
  }
  const reverse = ownEntry(map, leadImageUrl);
  if (
    reverse?.leadImageUrl === imageUrl &&
    isCastIdentityAnswer(reverse.identity)
  ) {
    return reverse.identity;
  }
  return null;
}

/**
 * `map` with the answer about `imageUrl` next to `leadImageUrl` set to
 * `identity`, or cleared when it is null. An answer about the same two
 * photos the other way round is dropped, so a pair never holds two.
 */
export function withCastIdentityAnswer(
  map: CastIdentityMap | null | undefined,
  imageUrl: string,
  leadImageUrl: string,
  identity: CastIdentityAnswer | null,
): CastIdentityMap {
  const next: CastIdentityMap = { ...(map ?? {}) };
  if (ownEntry(next, imageUrl)?.leadImageUrl === leadImageUrl) {
    delete next[imageUrl];
  }
  if (ownEntry(next, leadImageUrl)?.leadImageUrl === imageUrl) {
    delete next[leadImageUrl];
  }
  if (identity && photoUrl(imageUrl) && photoUrl(leadImageUrl)) {
    if (imageUrl !== leadImageUrl) {
      next[imageUrl] = { identity, leadImageUrl };
    }
  }
  return next;
}

/**
 * The answers about a cast of `lead` plus `extras`, each keyed by its extra
 * photo and given against `lead`: what a composer sends with createProject
 * and what the project stores. Answers about photos outside the cast, or
 * given against another main photo, are left out.
 */
export function castIdentityForCast(
  map: CastIdentityMap | null | undefined,
  lead: string | null | undefined,
  extras: readonly (string | null | undefined)[],
): CastIdentityMap {
  const out: CastIdentityMap = {};
  if (!lead) return out;
  for (const extra of extras) {
    if (!extra || extra === lead || ownEntry(out, extra)) continue;
    const identity = castIdentityAnswer(map, extra, lead);
    if (identity) out[extra] = { identity, leadImageUrl: lead };
  }
  return out;
}

export type CastIdentityOverride = {
  /** The profile's 1-based upload index (extra_character_N). */
  index: number;
  identity: CastIdentityAnswer;
};

/**
 * The user's answers applied over the vision verdict: `sameAsLead` false for
 * a photo the user called someone else, true for one they called the main
 * person. Matched on each profile's own photo URL and the main photo the
 * answer was given against; profiles without an answer are left alone.
 * Returns the same array when nothing changed.
 */
export function applyCastIdentityToProfiles<
  P extends { index: number; imageUrl: string; sameAsLead?: boolean },
>(
  profiles: readonly P[],
  map: CastIdentityMap | null | undefined,
  leadImageUrl: string | null | undefined,
): { profiles: P[]; overrides: CastIdentityOverride[] } {
  const overrides: CastIdentityOverride[] = [];
  const next = profiles.map((profile) => {
    const identity = castIdentityAnswer(map, profile.imageUrl, leadImageUrl);
    if (!identity) return profile;
    const sameAsLead = identity === "same_as_lead";
    // Absent means "not compared" and already behaves like false.
    if ((profile.sameAsLead === true) === sameAsLead) return profile;
    overrides.push({ index: profile.index, identity });
    return { ...profile, sameAsLead };
  });
  return { profiles: overrides.length ? next : (profiles as P[]), overrides };
}

/*
 * Upload time: the composers check each extra photo against the main photo
 * and show a note on a match.
 */

/**
 * Key of the check of the photo `imageUrl` against the main photo
 * `leadImageUrl`. Ordered on purpose: the vision call is shown the main
 * photo first, so only a check run this way round predicts the brief's
 * verdict after the two photos swap places.
 */
export function castPhotoCheckKey(
  imageUrl: string,
  leadImageUrl: string,
): string {
  return `${leadImageUrl}\n${imageUrl}`;
}

/**
 * Whether the same-person check applies to the photo `imageUrl` next to the
 * main photo `leadImageUrl`. The brief only compares two different photos
 * with a real main photo (a stock preset is a generated face nobody uploads
 * twice), and never two people cropped out of one group photo (different
 * by construction; see castPhotoSplitSiblings).
 */
export function castPhotoSameAsLeadCheckApplies(
  imageUrl: string | null | undefined,
  leadImageUrl: string | null | undefined,
): boolean {
  if (!imageUrl || !leadImageUrl || imageUrl === leadImageUrl) return false;
  if (isStockCharacterPresetUrl(leadImageUrl)) return false;
  return !castPhotoSplitSiblings(imageUrl, leadImageUrl);
}

/**
 * The note for the extra photo `imageUrl`: null unless the check matched it
 * to the main photo `leadImageUrl` (not checked yet, a different person, or
 * no verdict show nothing), else whether "It's someone else" is on.
 */
export function sameAsLeadCastPhotoNote(input: {
  imageUrl: string;
  leadImageUrl: string | null | undefined;
  /** The check's verdict for this pair; null = could not tell. */
  sameAsLead: boolean | null | undefined;
  answers: CastIdentityMap | null | undefined;
}): { someoneElse: boolean } | null {
  if (input.sameAsLead !== true) return null;
  if (!castPhotoSameAsLeadCheckApplies(input.imageUrl, input.leadImageUrl)) {
    return null;
  }
  return {
    someoneElse:
      castIdentityAnswer(input.answers, input.imageUrl, input.leadImageUrl) ===
      "someone_else",
  };
}

/** English copy of the note; mobile passes it to t() as defaults. */
export const SAME_AS_LEAD_CAST_PHOTO_COPY = {
  note: "Looks like the same person as your main photo",
  someoneElse: "It's someone else",
} as const;
